import React, { useState, useEffect } from 'react'
import { 
  IonContent, 
  IonPage, 
  IonButton, 
  IonCard,
  IonCardContent,
  IonIcon,
  IonProgressBar,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons
} from '@ionic/react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  trophyOutline, 
  journalOutline, 
  peopleOutline, 
  arrowForwardOutline,
  checkmarkCircleOutline
} from 'ionicons/icons'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../services/supabase'
import UserService from '../../services/userService'
import LanguageSwitcher from '../../components/LanguageSwitcher'

const OnboardingPage: React.FC = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const { user, setUser, supabaseUser } = useAuthStore()
  const [currentStep, setCurrentStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [onboardingJustCompleted, setOnboardingJustCompleted] = useState(false)

  // Handle navigation after onboarding completion
  useEffect(() => {
    if (onboardingJustCompleted && user?.onboarding_completed) {
      console.log('Onboarding completed, navigating to dashboard with user:', user)
      history.replace('/app/dashboard')
      setOnboardingJustCompleted(false)
    }
  }, [user?.onboarding_completed, onboardingJustCompleted, history, user])

  const steps = [
    {
      icon: peopleOutline,
      title: t('onboarding.welcome.title'),
      description: t('onboarding.welcome.description'),
      content: t('onboarding.welcome.content')
    },
    {
      icon: user?.role === 'coach' ? peopleOutline : journalOutline,
      title: user?.role === 'coach' ? t('onboarding.forCoaches.title') : t('onboarding.forCoachees.title'),
      description: user?.role === 'coach' ? t('onboarding.forCoaches.description') : t('onboarding.forCoachees.description'),
      content: user?.role === 'coach' ? t('onboarding.forCoaches.content') : t('onboarding.forCoachees.content')
    },
    {
      icon: trophyOutline,
      title: t('onboarding.getStarted.title'),
      description: t('onboarding.getStarted.description'),
      content: t('onboarding.getStarted.content')
    }
  ]

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      completeOnboarding()
    }
  }

  const completeOnboarding = async () => {
    if (!user && !supabaseUser) {
      console.error('No user found during onboarding completion')
      alert('Session expired. Please log in again.')
      history.push('/login')
      return
    }
    
    setLoading(true)
    try {
      // Use supabaseUser.id if user is null
      const userId = user?.id || supabaseUser?.id
      console.log('Completing onboarding for user:', userId)
      
      if (!userId) {
        throw new Error('No user ID available')
      }
      
      // Use the new complete_user_onboarding function that bypasses RLS
      console.log('Using complete_user_onboarding function to bypass RLS...')
      
      try {
        const { data, error } = await supabase.rpc('complete_user_onboarding', {
          profile_updates: {
            onboarding_completed: true,
            show_motivational_messages: true
          }
        })
        
        if (error) {
          console.error('complete_user_onboarding function error:', error)
          throw error
        }
        
        console.log('complete_user_onboarding function successful:', data)
        
        // Also update user metadata to persist across sessions
        const { error: metadataError } = await supabase.auth.updateUser({
          data: {
            onboarding_completed: true,
            show_motivational_messages: true
          }
        })
        
        if (metadataError) {
          console.warn('Failed to update user metadata, but profile was updated:', metadataError)
        } else {
          console.log('User metadata updated successfully')
        }
        
        // Update local state with the returned data
        if (data && data.length > 0) {
          const updatedProfile = data[0]
          const updatedUser = {
            ...user,
            ...updatedProfile,
            onboarding_completed: true,
            show_motivational_messages: true
          }
          setUser(updatedUser)
          setOnboardingJustCompleted(true)
        } else {
          // Fallback: update local state directly
          if (user) {
            const updatedUser = {
              ...user,
              onboarding_completed: true,
              show_motivational_messages: true
            }
            setUser(updatedUser)
            setOnboardingJustCompleted(true)
          }
        }
        
      } catch (functionError) {
        console.error('complete_user_onboarding function failed, trying fallback approaches:', functionError)
        
        // Fallback 1: Try admin function if user is admin
        if (user?.role === 'admin') {
          console.log('Admin user detected, trying admin function...')
          
          try {
            const { data, error } = await supabase.rpc('admin_update_user_profile', {
              target_user_id: userId,
              updates: {
                onboarding_completed: true,
                show_motivational_messages: true
              }
            })
            
            if (error) throw error
            
            console.log('Admin function update successful:', data)
            
            const updatedUser = {
              ...user,
              onboarding_completed: true,
              show_motivational_messages: true
            }
            setUser(updatedUser)
            setOnboardingJustCompleted(true)
            
          } catch (adminError) {
            console.error('Admin function also failed:', adminError)
            throw adminError
          }
        } else {
          // Fallback 2: Try UserService
          console.log('Attempting to update profile using UserService...')
          
          try {
            const updatedProfile = await UserService.updateProfile(userId, {
              onboarding_completed: true,
              show_motivational_messages: true
            })
            
            if (updatedProfile) {
              console.log('Profile updated successfully via UserService:', updatedProfile)
              setUser(updatedProfile)
              setOnboardingJustCompleted(true)
            } else {
              throw new Error('UserService returned null')
            }
          } catch (userServiceError) {
            console.error('UserService also failed:', userServiceError)
            
            // Fallback 3: Local state update with warning
            console.log('All database updates failed, using local state update workaround...')
            
            if (user) {
              const updatedUser = {
                ...user,
                onboarding_completed: true,
                show_motivational_messages: true
              }
              setUser(updatedUser)
              setOnboardingJustCompleted(true)
              
              // Show a warning that the database wasn't updated
              alert('Onboarding completed locally. Note: There was a database configuration issue, but you can continue using the app. The issue will be resolved in a future update.')
            } else {
              throw new Error('Cannot complete onboarding: no user data available')
            }
          }
        }
      }
      
      // Navigate to dashboard after successful completion
      console.log('Onboarding completed successfully, navigating to dashboard...')
      setTimeout(() => {
        if (user?.role === 'admin') {
          history.push('/app/admin')
        } else {
          history.push('/app/dashboard')
        }
      }, 1000)
      
    } catch (error) {
      console.error('Error completing onboarding:', error)
      let errorMessage = 'Unknown error occurred'
      
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as any).message
      }
      
      // For development, show more detailed error info
      if (import.meta.env.DEV) {
        console.error('Full error object:', error)
        alert(`Failed to complete onboarding: ${errorMessage}\n\nCheck console for more details.`)
      } else {
        alert(`Failed to complete onboarding: ${errorMessage}. Please try again.`)
      }
    } finally {
      setLoading(false)
    }
  }

  const progress = ((currentStep + 1) / steps.length) * 100

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('onboarding.title', 'Welcome to InsightFlow')}</IonTitle>
          <IonButtons slot="end">
            <LanguageSwitcher />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50">
          <div className="max-w-2xl mx-auto pt-12 px-4">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-600">
                  {t('onboarding.step')} {currentStep + 1} {t('onboarding.of')} {steps.length}
                </span>
                <span className="text-sm font-medium text-primary-600">
                  {Math.round(progress)}%
                </span>
              </div>
              <IonProgressBar value={progress / 100} color="primary" />
            </div>

            {/* Content Card */}
            <IonCard className="shadow-lg">
              <IonCardContent className="p-8 text-center">
                <div className="bg-primary-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <IonIcon 
                    icon={steps[currentStep].icon} 
                    className="text-3xl text-primary-600"
                  />
                </div>
                
                <h1 className="text-3xl font-heading font-bold text-gray-900 mb-4">
                  {steps[currentStep].title}
                </h1>
                
                <p className="text-xl text-gray-600 mb-6">
                  {steps[currentStep].description}
                </p>
                
                <div className="text-gray-700 mb-8 text-left space-y-4">
                  {steps[currentStep].content}
                </div>

                {/* Navigation Buttons */}
                <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-4">
                  <div className="flex justify-center sm:justify-start">
                    {currentStep > 0 && (
                      <IonButton 
                        fill="clear" 
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-full sm:w-auto"
                      >
                        {t('common.previous')}
                      </IonButton>
                    )}
                  </div>
                  
                  <IonButton 
                    size="large"
                    onClick={handleNext}
                    disabled={loading}
                    className="w-full sm:w-auto"
                  >
                    {loading ? (
                      t('common.loading')
                    ) : currentStep === steps.length - 1 ? (
                      <>
                        <IonIcon icon={checkmarkCircleOutline} slot="start" />
                        {t('onboarding.complete')}
                      </>
                    ) : (
                      <>
                        {t('common.next')}
                        <IonIcon icon={arrowForwardOutline} slot="end" />
                      </>
                    )}
                  </IonButton>
                </div>
              </IonCardContent>
            </IonCard>

            {/* Skip Button */}
            <div className="text-center mt-6">
              <IonButton 
                fill="clear" 
                size="small"
                onClick={completeOnboarding}
                disabled={loading}
              >
                {t('onboarding.skip')}
              </IonButton>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  )
}

export default OnboardingPage 